import { useDeepEffect } from '@domain-group/react-hooks'
import {
  Dispatch, MutableRefObject, SetStateAction, useRef, useState,
} from 'react'

type ModifySortParams = {
  fromIndex: number,
  toIndex: number,
  setSortedIds: Dispatch<SetStateAction<number[]>>,
  updateSort?: (idArray: number[]) => void | Promise<unknown>,
  pendingSort: MutableRefObject<Promise<unknown>>,
}

const modifySort = (params: ModifySortParams) => {
  const {
    fromIndex, toIndex, setSortedIds, updateSort, pendingSort,
  } = params

  setSortedIds((currentIds) => {
    const sortedIds = [...currentIds]
    const [removedItemId] = sortedIds.splice(fromIndex, 1)
    sortedIds.splice(toIndex, 0, removedItemId)

    if (updateSort){
      // Chain onto any in-flight save so requests reach the server in drop order,
      // instead of firing a new PUT before the previous one resolves.
      pendingSort.current = pendingSort.current
        .catch(() => {})
        .then(() => updateSort(sortedIds))
    }

    return sortedIds
  })
}

type IdSortParams = {
  callbacks?: {
    updateSort: (idArray: number[]) => void | Promise<unknown>,
  },
  idsArray: number[],
}

function useIdSort(params: IdSortParams) {
  const { callbacks, idsArray = [] } = params
  const { updateSort } = callbacks || {}

  const [sortedIds, setSortedIds] = useState<number[]>([])
  const pendingSort = useRef<Promise<unknown>>(Promise.resolve())

  useDeepEffect(() => {
    if (idsArray) setSortedIds(idsArray)
  }, [idsArray])

  return {
    callbacks: {
      modifySort: (fromIndex: number, toIndex: number) => modifySort({
        fromIndex, toIndex, setSortedIds, updateSort, pendingSort,
      }),
    },
    sortedIds,
  }
}

export default useIdSort
